import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type Look, lookProps, lookSurface } from '@/entities/appearance';
import { type Board, boardColor, useSetBoardAppearance } from '@/entities/board';
import { isLookValid, LookEditor, lookPayload } from '@/features/appearance-editor';
import { FormError } from '@/shared/forms';
import { Button, Dialog, DialogActions } from '@/shared/ui';

import styles from './BoardAppearanceDialog.module.css';
import { TILE_STYLES } from './tile-styles';

type Target = Pick<Board, 'id' | 'name' | 'color' | 'appearance'>;

/**
 * The look of a board's CARD in catalogs (Home and the workspace) - nothing else. The opened
 * board's background (the other half of the board's appearance) is kept exactly as it is and is
 * changed in the board's own appearance dialog; the board's semantic color stays visible.
 */
export function BoardCardAppearanceDialog({
  board,
  onClose,
}: {
  board: Target;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const save = useSetBoardAppearance(board.id);
  const initial = board.appearance?.tile ?? null;
  const [tile, setTile] = useState<Look | null>(initial);
  const payload = lookPayload(tile);
  const props = lookProps(payload);
  const apply = (next: Look | null) => {
    save.mutate(
      { v: 1, tile: next, background: board.appearance?.background ?? null },
      { onSuccess: onClose },
    );
  };
  return (
    <Dialog
      title={t('boards.cardAppearanceTitle', { name: board.name })}
      onClose={onClose}
      size="lg"
    >
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          apply(payload);
        }}
      >
        <div
          className={`${styles.tilePreview} ${lookSurface ?? ''}`}
          data-testid="board-tile-preview"
          aria-label={t('boards.previewTile')}
          role="img"
          {...props}
          style={{ ...props.style, ['--board-color' as string]: boardColor(board) }}
        >
          <span className={styles.tileColor} aria-hidden />
          <strong>{board.name}</strong>
        </div>
        <p className={styles.hint}>{t('boards.cardAppearanceHint')}</p>
        <LookEditor value={tile} onChange={setTile} allowed={TILE_STYLES} idPrefix="card" />
        <FormError error={save.error} />
        <DialogActions>
          <Button
            variant="ghost"
            onClick={() => {
              setTile(null);
            }}
          >
            {t('boards.resetAppearance')}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={save.isPending || !isLookValid(tile)}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
